/** MA stack pills — scanner table, watchlist, Android (via sync). */
import React from "react";
import { formatMaPill } from "./movingAverages.js";

function pillStyle(above, T) {
  if (above == null) return { bg: T.surfaceHi, color: T.textDim, border: T.border };
  return above
    ? { bg: `${T.green}22`, color: T.green, border: `${T.green}55` }
    : { bg: `${T.red}18`, color: T.red, border: `${T.red}44` };
}

function maTooltip(period, ema, pct, slope, price) {
  const parts = [`EMA${period}`];
  if (ema != null) parts.push(`$${ema.toFixed(2)}`);
  if (pct != null) parts.push(`${pct >= 0 ? "+" : ""}${pct.toFixed(1)}% vs price`);
  if (slope != null) parts.push(`5d slope ${slope >= 0 ? "+" : ""}${slope.toFixed(2)}%`);
  if (price != null) parts.push(`price $${price.toFixed(2)}`);
  return parts.join(" · ");
}

export function MaStackPills({ result, T, vertical = false }) {
  const ma = result?.ma;
  const price = result?.price;
  if (!ma?.ema50 && !ma?.ema20 && !ma?.ema200) return <span style={{ color: T.textDim, fontSize: 9 }}>—</span>;

  const rows = [
    { p: 20, ema: ma.ema20, pct: ma.pctFrom20, slope: ma.slope20 },
    { p: 50, ema: ma.ema50, pct: ma.pctFrom50, slope: ma.slope50 },
    { p: 200, ema: ma.ema200, pct: ma.pctFrom200, slope: ma.slope200 },
  ];

  return (
    <div
      style={{
        display: "flex",
        flexDirection: vertical ? "column" : "row",
        flexWrap: "wrap",
        gap: 3,
        justifyContent: "center",
      }}
    >
      {rows.map(({ p, ema, pct, slope }) => {
        const { above, arrow } = formatMaPill(price, ema, slope);
        const st = pillStyle(above, T);
        return (
          <span
            key={p}
            title={maTooltip(p, ema, pct, slope, price)}
            style={{
              fontSize: 8,
              fontWeight: 800,
              padding: "2px 4px",
              borderRadius: 4,
              border: `1px solid ${st.border}`,
              background: st.bg,
              color: st.color,
              fontFamily: "'JetBrains Mono', monospace",
              whiteSpace: "nowrap",
              cursor: "help",
            }}
          >
            {p}
            {arrow}
          </span>
        );
      })}
    </div>
  );
}

export function matchesAboveAllMas(result) {
  const ma = result?.ma;
  if (!ma || result?.price == null) return false;
  const p = result.price;
  return (
    ma.ema20 != null &&
    ma.ema50 != null &&
    ma.ema200 != null &&
    p > ma.ema20 &&
    p > ma.ema50 &&
    p > ma.ema200 &&
    (ma.slope20 ?? 0) > 0 &&
    (ma.slope50 ?? 0) > 0 &&
    (ma.slope200 ?? 0) > 0
  );
}

export function matchesTestingEma50(result) {
  const ma = result?.ma;
  if (!ma?.ema50 || result?.price == null) return false;
  const dist = Math.abs(result.price - ma.ema50) / ma.ema50;
  return dist <= 0.02 && (ma.slope50 ?? 0) > 0;
}
